import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { initials } from '../lib/fmt';
import { InviteForm } from '../components/EntityForms';

type Account = { id: string; user_id: string; app_role: string; status: string; parties: { display_name: string | null } | null };
type Invite = { id: string; email: string; app_role: string | null; status: string; expires_at: string | null };

const ROLE_TONE: Record<string, 'inprogress' | 'open' | 'done' | 'onhold'> = {
  admin: 'inprogress', manager: 'done', finance: 'inprogress', technician: 'open', subcontractor: 'open',
  owner: 'onhold', villa_manager: 'onhold', management_partner: 'onhold', referrer: 'open',
};
const ACCOUNT_TONE: Record<string, 'inprogress' | 'open' | 'done' | 'onhold' | 'overdue'> = {
  active: 'done', suspended: 'onhold', disabled: 'overdue',
};
const INVITE_TONE: Record<string, 'inprogress' | 'open' | 'done' | 'onhold' | 'overdue'> = {
  pending: 'onhold', accepted: 'done', revoked: 'open', expired: 'overdue',
};

export function Users() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [a, inv] = await Promise.all([
      supabase.from('app_accounts').select('id,user_id,app_role,status,parties(display_name)').is('deleted_at', null).order('app_role'),
      supabase.from('invitations').select('id,email,app_role,status,expires_at').is('deleted_at', null).neq('status', 'accepted').order('created_at', { ascending: false }),
    ]);
    setAccounts((a.data as any as Account[]) ?? []);
    setInvites((inv.data as Invite[]) ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function setStatus(id: string, status: string) {
    await supabase.rpc('set_account_status', { p_account: id, p_status: status });
    load();
  }
  async function revoke(id: string) {
    await supabase.rpc('revoke_invitation', { p_id: id });
    load();
  }
  async function resend(id: string) {
    const r = await supabase.rpc('resend_invitation', { p_id: id });
    if (!r.error && r.data) {
      const base = r.data.app_key === 'field' ? ((import.meta as any).env?.VITE_FIELD_URL ?? '')
        : r.data.app_key === 'portal' ? ((import.meta as any).env?.VITE_PORTAL_URL ?? '')
        : window.location.origin;
      setLink(`${base}/set-password?token=${r.data.token}`);
    }
    load();
  }

  return (
    <Layout header={<Topbar actions={<Button onClick={() => setFormOpen(true)}>+ {t('form.inviteUser')}</Button>} />}>
      <div className="px-7 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.users')}</h1>
      </div>
      <div className="flex flex-col gap-[18px] px-7 pb-7 pt-[18px]">
        {link && (
          <Card className="flex items-center justify-between gap-3 p-3 text-sm">
            <span className="min-w-0 flex-1 truncate font-mono text-xs">{link}</span>
            <button className="shrink-0 rounded-md border border-border px-3 py-1.5" onClick={() => void navigator.clipboard?.writeText(link)}>{t('invite.copy')}</button>
            <button className="shrink-0 text-muted" onClick={() => setLink(null)}>✕</button>
          </Card>
        )}
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="px-5 py-3 font-medium">{t('col.name')}</th>
                  <th className="px-5 py-3 font-medium">{t('users.role')}</th>
                  <th className="px-5 py-3 font-medium">{t('users.status')}</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="px-5 py-6 text-muted">{t('common.loading')}</td></tr>
                ) : accounts.length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>
                ) : (
                  accounts.map((a) => {
                    const name = a.parties?.display_name ?? a.user_id.slice(0, 8);
                    return (
                      <tr key={a.id} className="border-t border-divider hover:bg-bg">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <span className="grid h-7 w-7 place-items-center rounded-full bg-segmented font-mono text-2xs">{initials(name)}</span>
                            <span className="font-medium">{name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5"><StatusPill tone={ROLE_TONE[a.app_role] ?? 'open'}>{t('appRole.' + a.app_role)}</StatusPill></td>
                        <td className="px-5 py-3.5"><StatusPill tone={ACCOUNT_TONE[a.status] ?? 'open'}>{t('accountStatus.' + a.status)}</StatusPill></td>
                        <td className="px-5 py-3.5 text-right">
                          {a.status === 'active' && <Button variant="ghost" size="sm" onClick={() => setStatus(a.id, 'suspended')}>{t('users.suspend')}</Button>}
                          {a.status !== 'active' && <Button variant="ghost" size="sm" onClick={() => setStatus(a.id, 'active')}>{t('users.reactivate')}</Button>}
                          {a.status !== 'disabled' && <Button variant="ghost" size="sm" onClick={() => setStatus(a.id, 'disabled')}>{t('users.disable')}</Button>}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {invites.length > 0 && (
          <div className="flex flex-col gap-3">
            <h2 className="eyebrow">{t('form.pendingInvites')}</h2>
            <Card className="overflow-hidden p-0">
              <table className="w-full border-collapse text-sm">
                <tbody>
                  {invites.map((i) => (
                    <tr key={i.id} className="border-t border-divider first:border-0">
                      <td className="px-5 py-3 font-medium">{i.email}</td>
                      <td className="px-2 py-3">{i.app_role && <StatusPill tone={ROLE_TONE[i.app_role] ?? 'open'}>{t('appRole.' + i.app_role)}</StatusPill>}</td>
                      <td className="px-2 py-3"><StatusPill tone={INVITE_TONE[i.status] ?? 'open'}>{t('inviteStatus.' + i.status)}</StatusPill></td>
                      <td className="px-5 py-3 text-right">
                        {i.status === 'pending' && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => resend(i.id)}>{t('invite.resend')}</Button>
                            <Button variant="ghost" size="sm" onClick={() => revoke(i.id)}>{t('form.revoke')}</Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        )}
      </div>
      <InviteForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} />
    </Layout>
  );
}
